import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useYearContext } from '../contexts/YearContext';
import { Select } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { getQueryFn } from '../lib/queryClient';
import type { Country } from '../types';

interface YearControlsProps {
  showComparison?: boolean;
  showBaseYear?: boolean;
  showCountryFilter?: boolean;
  compact?: boolean;
  className?: string;
}

export function YearControls({ 
  showComparison = true, 
  showBaseYear = true, 
  showCountryFilter = false,
  compact = false,
  className = '' 
}: YearControlsProps) {
  const {
    baseYear,
    selectedYear,
    comparisonYear,
    availableYears,
    setSelectedYear,
    setComparisonYear,
    isComparisonMode,
    currentCompany,
    companies,
    setCurrentCompany,
  } = useYearContext();

  if (!currentCompany || !availableYears || availableYears.length === 0) {
    return null;
  }

  const handleClearComparison = () => {
    setComparisonYear(null);
  };

  const handleToggleComparison = () => {
    if (isComparisonMode) {
      setComparisonYear(null);
    } else {
      // Set comparison year to base year if available
      setComparisonYear(baseYear);
    }
  };

  if (compact) {
    return (
      <div className={`flex items-center space-x-4 ${className}`}>
        {showBaseYear && (
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-600">Base Year:</label>
            <span className="text-sm font-semibold text-blue-600">{baseYear}</span>
          </div>
        )}
        
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-600">Reporting Year:</label>
          <Select
            value={selectedYear?.toString() || ''}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="w-24"
            options={[
              { value: '', label: 'Select' },
              ...availableYears.map(year => ({ value: year.toString(), label: year.toString() }))
            ]}
          />
        </div>

        {showComparison && (
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-600">Compare:</label>
            <Select
              value={comparisonYear?.toString() || ''}
              onChange={(e) => setComparisonYear(parseInt(e.target.value))}
              className="w-24"
              options={[
                { value: '', label: 'None' },
                ...availableYears.filter(year => year !== selectedYear).map(year => ({ 
                  value: year.toString(), 
                  label: year.toString() 
                }))
              ]}
            />
          </div>
        )}

        {isComparisonMode && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearComparison}
            className="text-xs"
          >
            Clear
          </Button>
        )}
      </div>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center text-lg">
          <i data-feather="calendar" className="w-5 h-5 mr-2"></i>
          Year Context
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Company Selector */}
        {companies && companies.length > 1 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company
            </label>
            <Select
              value={currentCompany?.id?.toString() || ''}
              onChange={(e) => {
                const companyId = parseInt(e.target.value);
                const company = companies.find(c => c.id === companyId);
                if (company) setCurrentCompany(company);
              }}
              options={companies.map(company => ({
                value: company.id.toString(),
                label: company.name
              }))}
            />
          </div>
        )}

        {showCountryFilter && currentCompany?.assigned_countries && currentCompany.assigned_countries.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country Filter (from company registration)
            </label>
            <Select
              value=""
              onChange={(e) => {
                // Handle country filter change
                console.log('Country filter:', e.target.value);
              }}
              options={[
                { value: '', label: 'All Countries' },
                ...currentCompany.assigned_countries.map((country: Country) => ({
                  value: country.code,
                  label: country.name
                }))
              ]}
            />
          </div>
        )}

        {showBaseYear && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Base Year (from company settings)
            </label>
            <div className="flex items-center space-x-2">
              <span className="text-lg font-semibold text-blue-600">{baseYear}</span>
              <span className="text-sm text-gray-500">(read-only)</span>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reporting Year
          </label>
          <Select
            value={selectedYear?.toString() || ''}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="w-full"
            options={[
              { value: '', label: 'Select a year' },
              ...availableYears.map(year => ({ value: year.toString(), label: year.toString() }))
            ]}
          />
        </div>

        {showComparison && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Comparison Year
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleComparison}
                className="text-xs"
              >
                {isComparisonMode ? 'Clear Comparison' : 'Add Comparison'}
              </Button>
            </div>
            
            <Select
              value={comparisonYear?.toString() || ''}
              onChange={(e) => setComparisonYear(parseInt(e.target.value))}
              className="w-full"
              disabled={!isComparisonMode}
              options={[
                { value: '', label: 'No comparison' },
                ...availableYears.filter(year => year !== selectedYear).map(year => ({ 
                  value: year.toString(), 
                  label: year.toString() 
                }))
              ]}
            />
          </div>
        )}

        {isComparisonMode && (
          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <div className="flex items-center space-x-2">
              <i data-feather="info" className="w-4 h-4 text-blue-600"></i>
              <span className="text-sm text-blue-800">
                Comparing {selectedYear} vs {comparisonYear}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Compact year selector for use in headers/navbars
export function YearSelector({ className = '' }: { className?: string }) {
  const {
    selectedYear,
    availableYears,
    setSelectedYear,
  } = useYearContext();

  if (!availableYears || availableYears.length === 0) {
    return null;
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <label className="text-sm text-gray-600">Year:</label>
      <Select
        value={selectedYear?.toString() || ''}
        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
        className="w-20"
        options={availableYears.map(year => ({ value: year.toString(), label: year.toString() }))}
      />
    </div>
  );
}
